import { Box, Chip, Typography } from "@mui/material";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { BooleanIcon } from "../../../../components/ui/booleanIcon/booleanIcon.component";
import { ServiceReminderPlan } from "../../../../types/contracts";

type ServiceReminderPlanSummaryProps = {
  plan?: ServiceReminderPlan | null;
};

/**
 * Read-only view of a sale term's obligatory service reminders: how the plan
 * is set up, then every reminder on it - sent and completed ones included, as
 * they are the record of services that did (or did not) happen.
 */
export const ServiceReminderPlanSummary = ({ plan }: ServiceReminderPlanSummaryProps) => {
  const { t } = useTranslation();

  if (!plan) {
    return <Typography variant="subtitle1">-</Typography>;
  }

  const reminders = [...(plan.reminders || [])].sort((a, b) =>
    a.due_date.localeCompare(b.due_date)
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
      {plan.is_active === false ? (
        <Box>
          <Chip label={t("Disabled")} size="small" />
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {[
            plan.interval_months ? `${t("Every (months)")}: ${plan.interval_months}` : null,
            plan.lead_time_days != null
              ? `${t("Remind this many days ahead")}: ${plan.lead_time_days}`
              : null,
            <>
              {t("Create a service job automatically")}:{" "}
              <BooleanIcon value={Boolean(plan.auto_create_job)} />
            </>,
          ]
            .filter(Boolean)
            .map((part, index) => (
              <Fragment key={index}>
                {index > 0 && " · "}
                {part}
              </Fragment>
            ))}
        </Typography>
      )}

      {reminders.length === 0 ? (
        <Typography variant="subtitle1">-</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {reminders.map((reminder) => (
            <Box
              key={reminder.id ?? reminder.due_date}
              sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ color: reminder.is_overdue ? "error.main" : undefined }}
              >
                {reminder.due_date}
              </Typography>
              {reminder.status_display && (
                <Chip label={reminder.status_display} size="small" variant="outlined" />
              )}
              {reminder.notify_at && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("Client is told on")}: {reminder.notify_at}
                </Typography>
              )}
              {reminder.job_number && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("Job")}: {reminder.job_number}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
